"use client"
import { useRef, useState, useEffect } from "react";
import * as htmlToImage from "html-to-image";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Upload, Home, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";

// Data untuk 27 Tritype
const tritypes = [
    { id: "125", name: "The Mentor", keywords: "Diligent, Caring, and Knowledgeable", color: "#F44336" },
    { id: "126", name: "The Supporter", keywords: "Diligent, Caring, and Engaging", color: "#F44336" },
    { id: "127", name: "The Teacher", keywords: "Diligent, Caring, and Innovative", color: "#F44336" },
    { id: "135", name: "The Technical Expert", keywords: "Diligent, Ambitious, and Knowledgeable", color: "#FF9800" },
    { id: "136", name: "The Taskmaster", keywords: "Diligent, Ambitious, and Engaging", color: "#FF9800" },
    { id: "137", name: "The Systems Builder", keywords: "Diligent, Ambitious, and Innovative", color: "#FF9800" },
    { id: "145", name: "The Researcher", keywords: "Diligent, Intuitive, and Knowledgeable", color: "#FFEB3B" },
    { id: "146", name: "The Philosopher", keywords: "Diligent, Intuitive, and Engaging", color: "#FFEB3B" },
    { id: "147", name: "The Visionary", keywords: "Diligent, Intuitive, and Innovative", color: "#FFEB3B" },
    { id: "258", name: "The Strategist", keywords: "Caring, Knowledgeable, and Protective", color: "#4CAF50" },
    { id: "259", name: "The Problem Solver", keywords: "Caring, Knowledgeable, and Accepting", color: "#4CAF50" },
    { id: "268", name: "The Rescuer", keywords: "Caring, Engaging, and Protective", color: "#4CAF50" },
    { id: "269", name: "The Good Samaritan", keywords: "Caring, Engaging, and Accepting", color: "#00BCD4" },
    { id: "278", name: "The Free Spirit", keywords: "Caring, Innovative, and Protective", color: "#00BCD4" },
    { id: "279", name: "The Peacemaker", keywords: "Caring, Innovative, and Accepting", color: "#00BCD4" },
    { id: "358", name: "The Solution Master", keywords: "Ambitious, Knowledgeable, and Protective", color: "#2196F3" },
    { id: "359", name: "The Thinker", keywords: "Ambitious, Knowledgeable, and Accepting", color: "#2196F3" },
    { id: "368", name: "The Justice Fighter", keywords: "Ambitious, Engaging, and Protective", color: "#2196F3" },
    { id: "369", name: "The Mediator", keywords: "Ambitious, Engaging, and Accepting", color: "#9C27B0" },
    { id: "378", name: "The Mover Shaker", keywords: "Ambitious, Innovative, and Protective", color: "#9C27B0" },
    { id: "379", name: "The Ambassador", keywords: "Ambitious, Innovative, and Accepting", color: "#9C27B0" },
    { id: "458", name: "The Scholar", keywords: "Intuitive, Knowledgeable, and Protective", color: "#E91E63" },
    { id: "459", name: "The Contemplative", keywords: "Intuitive, Knowledgeable, and Accepting", color: "#E91E63" },
    { id: "468", name: "The Truth Teller", keywords: "Intuitive, Engaging, and Protective", color: "#E91E63" },
    { id: "469", name: "The Seeker", keywords: "Intuitive, Engaging, and Accepting", color: "#673AB7" },
    { id: "478", name: "The Messenger", keywords: "Intuitive, Innovative, and Protective", color: "#673AB7" },
    { id: "479", name: "The Gentle Spirit", keywords: "Intuitive, Innovative, and Accepting", color: "#673AB7" },
];

const availableFonts = [
  { name: "Poppins", value: "'Poppins', sans-serif", url: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;700&display=swap" },
  { name: "Playfair Display", value: "'Playfair Display', serif", url: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&display=swap" },
  { name: "Roboto Mono", value: "'Roboto Mono', monospace", url: "https://fonts.googleapis.com/css2?family=Roboto+Mono&display=swap" },
];

const FORM_STEPS = [
    { id: 1, title: "Pilih Tritype & Teks", description: "Pilih Tritype dan atur teks Anda." },
    { id: 2, title: "Unggah & Atur Gambar", description: "Pilih 4 gambar dan sesuaikan posisinya." },
    { id: 3, title: "Kustomisasi Simbol", description: "Atur tampilan simbol Enneagram." },
    { id: 4, title: "Unduh", description: "Simpan hasil akhir moodboard Anda." },
];

const EnneagramSymbol = ({ color, tritypeId, bgImage, fontColor }) => {
    const points = [...Array(9)].map((_, i) => {
        const angle = -Math.PI / 2 + (i * 2 * Math.PI) / 9;
        return { x: 50 + 45 * Math.cos(angle), y: 50 + 45 * Math.sin(angle) };
    });

    const trianglePath = `M ${points[8].x} ${points[8].y} L ${points[2].x} ${points[2].y} L ${points[5].x} ${points[5].y} Z`;
    const hexadPath = `M ${points[0].x} ${points[0].y} L ${points[3].x} ${points[3].y} L ${points[1].x} ${points[1].y} L ${points[7].x} ${points[7].y} L ${points[4].x} ${points[4].y} L ${points[6].x} ${points[6].y} Z`;
    
    const validTritypeId = typeof tritypeId === 'string' && tritypeId.length === 3 ? tritypeId : '479';
    const tritypePoints = validTritypeId.split('').map(n => points[parseInt(n) - 1]);
    const tritypePath = `M ${tritypePoints[0].x} ${tritypePoints[0].y} L ${tritypePoints[1].x} ${tritypePoints[1].y} L ${tritypePoints[2].x} ${tritypePoints[2].y} Z`;

    return (
        <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full h-full rounded-full" style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(5px)', border: `2px solid ${color}` }}>
                <svg viewBox="0 0 100 100" className="w-full h-full">
                    <defs>
                        <filter id="glow"><feGaussianBlur stdDeviation="1.5" result="coloredBlur"/><feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
                        <clipPath id="circleClip"><circle cx="50" cy="50" r="48"/></clipPath>
                    </defs>
                    {bgImage && <image href={bgImage} x="0" y="0" width="100" height="100" clipPath="url(#circleClip)" />}
                    <circle cx="50" cy="50" r="48" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.5" />
                    <path d={trianglePath} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
                    <path d={hexadPath} fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
                    <path d={tritypePath} fill="none" stroke={color} strokeWidth="1.2" filter="url(#glow)" />
                    {points.map((p, i) => (
                        <text key={i} x={p.x} y={p.y} fill={fontColor} fontSize="6" textAnchor="middle" dy="2" fontWeight="normal">
                            {i + 1}
                        </text>
                    ))}
                </svg>
            </div>
        </div>
    );
};

const initialSettings = {
    font: availableFonts[0].value,
    nameColor: "#FFFFFF",
    keywordsColor: "#FFFFFF",
    showTextBg: false,
    textBgColor: "#000000",
    textBgOpacity: 0.5,
    textBgPadding: 10,
    textBgBorder: false,
    textBgBorderColor: "#FFFFFF",
    textBgBorderWidth: 1,
    enneagramBgImage: null,
    enneagramOpacity: 1,
    enneagramNumberColor: "#FFFFFF",
    useCustomText: false,
    customTritype: "479",
    customName: "The Gentle Spirit",
    customKeywords: "Intuitive, Innovative, and Accepting",
    useCustomLineColor: false,
    customLineColor: "#FF00FF",
};

const initialImageTransforms = {
    topLeft: { scale: 1, x: 0, y: 0 },
    topRight: { scale: 1, x: 0, y: 0 },
    bottomLeft: { scale: 1, x: 0, y: 0 },
    bottomRight: { scale: 1, x: 0, y: 0 },
};

export default function TritypeMoodGenerator() {
    const canvasRef = useRef(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedTritype, setSelectedTritype] = useState(tritypes[tritypes.length - 1]);
    const [images, setImages] = useState({ topLeft: null, topRight: null, bottomLeft: null, bottomRight: null });
    const [settings, setSettings] = useState(initialSettings);
    const [imageTransforms, setImageTransforms] = useState(initialImageTransforms);

    const currentFontUrl = availableFonts.find(f => f.value === settings.font)?.url;

    const handleSettingChange = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));
    const handleImageTransformChange = (pos, key, value) => setImageTransforms(prev => ({ ...prev, [pos]: { ...prev[pos], [key]: value } }));

    const handleImageUpload = (e, position) => {
        if (e.target.files && e.target.files[0]) {
            const url = URL.createObjectURL(e.target.files[0]);
            if (position === 'enneagramBg') {
                handleSettingChange('enneagramBgImage', url);
            } else {
                setImages(prev => ({ ...prev, [position]: url }));
                setImageTransforms(prev => ({ ...prev, [position]: { scale: 1, x: 0, y: 0 } }));
            }
        }
    };

    const downloadImage = async () => {
        if (!canvasRef.current) return;
        try {
            const dataUrl = await htmlToImage.toPng(canvasRef.current, { 
                canvasWidth: 1000,
                canvasHeight: 1000,
                pixelRatio: 1,
            });
            const link = document.createElement("a");
            link.download = `Tritype-${settings.useCustomText ? settings.customTritype : selectedTritype.id}.png`;
            link.href = dataUrl;
            link.click();
        } catch (error) {
            console.error("Gagal mengunduh gambar:", error);
            alert("Gagal mengunduh gambar. Silakan coba lagi.");
        }
    };

    const resetAll = () => {
        setSettings(initialSettings);
        setImages({ topLeft: null, topRight: null, bottomLeft: null, bottomRight: null });
        setImageTransforms(initialImageTransforms);
        setSelectedTritype(tritypes[tritypes.length - 1]);
        setCurrentStep(1);
    };

    const nextStep = () => { if (currentStep < FORM_STEPS.length) setCurrentStep(currentStep + 1); };
    const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };
    
    const ImageUploader = ({ position, imageUrl, label, transform, onTransformChange }) => (
        <div className="space-y-2">
            <div className="relative w-full aspect-square bg-gray-200 flex items-center justify-center text-gray-400 overflow-hidden rounded-lg">
                <div className="w-full h-full">
                    {imageUrl ? <img src={imageUrl} alt={`${label} preview`} className="w-full h-full object-cover" style={{transform: `scale(${transform.scale}) translateX(${transform.x}%) translateY(${transform.y}%)`}}/> : <div className="text-center pt-8"><Upload size={32} className="mx-auto"/><p className="text-xs mt-1">{label}</p></div>}
                </div>
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, position)} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"/>
            </div>
            {imageUrl && (
                <details className="text-xs">
                    <summary className="cursor-pointer font-medium">Atur Gambar</summary>
                    <div className="p-2 bg-gray-100 rounded-md mt-1 space-y-1">
                        <label>Zoom: {transform.scale.toFixed(2)}x</label>
                        <input type="range" min="1" max="3" step="0.05" value={transform.scale} onChange={(e) => onTransformChange(position, 'scale', parseFloat(e.target.value))} className="w-full"/>
                        <label>Geser Horizontal: {transform.x}%</label>
                        <input type="range" min="-50" max="50" step="1" value={transform.x} onChange={(e) => onTransformChange(position, 'x', parseInt(e.target.value))} className="w-full"/>
                        <label>Geser Vertikal: {transform.y}%</label>
                        <input type="range" min="-50" max="50" step="1" value={transform.y} onChange={(e) => onTransformChange(position, 'y', parseInt(e.target.value))} className="w-full"/>
                    </div>
                </details>
            )}
        </div>
    );

    const renderStepContent = () => {
        // ... (Fungsi ini tidak diubah)
    };

    const tritypeToDisplay = settings.useCustomText 
        ? { id: settings.customTritype, name: settings.customName, keywords: settings.customKeywords, color: settings.useCustomLineColor ? settings.customLineColor : selectedTritype.color }
        : selectedTritype;

    return (
        <div className="min-h-screen w-full bg-gray-100 p-4 sm:p-6 lg:p-8 font-sans">
            {currentFontUrl && <style>{`@import url('${currentFontUrl}');`}</style>}
            <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                    <div><h1 className="text-3xl font-bold text-gray-800">Tritype Mood Generator</h1><p className="text-gray-500">Buat moodboard visual berdasarkan Tritype Enneagram Anda.</p></div>
                    <Link to="/" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-300 transition-colors"><Home size={16} /><span>Kembali</span></Link>
                </div>
                <div className="grid lg:grid-cols-2 gap-8 items-start">
                    <div className="flex flex-col h-full">
                        <div className="mb-4"><h2 className="text-xl font-bold text-gray-800">{FORM_STEPS[currentStep - 1]?.title}</h2><p className="text-gray-500 text-sm">{FORM_STEPS[currentStep - 1]?.description}</p></div>
                        <div className="flex-grow p-4 bg-gray-50 rounded-lg min-h-[300px]"><AnimatePresence mode="wait"><motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>{renderStepContent()}</motion.div></AnimatePresence></div>
                        <div className="flex justify-between pt-6 mt-auto">
                            <div>
                                <button onClick={prevStep} disabled={currentStep === 1} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-600 text-white hover:bg-gray-700 disabled:bg-gray-200 disabled:cursor-not-allowed"><ChevronLeft size={16} /> Kembali</button>
                            </div>
                            <button onClick={resetAll} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600"><RotateCcw size={16} /> Reset</button>
                            <button onClick={nextStep} disabled={currentStep === FORM_STEPS.length} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed">Lanjut <ChevronRight size={16} /></button>
                        </div>
                    </div>
                    {/* --- PERBAIKAN: Tata letak pratinjau disederhanakan dan dibuat responsif --- */}
                    <div className="flex flex-col items-center justify-start lg:sticky top-8">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Pratinjau Langsung</h3>
                        <div className="w-full max-w-[500px] aspect-square mx-auto relative">
                            <div ref={canvasRef} className="absolute top-0 left-0 w-[1000px] h-[1000px] bg-black text-white" style={{fontFamily: settings.font, transform: 'scale(0.5)', transformOrigin: 'top left'}}>
                                <div className="grid grid-cols-2 grid-rows-2 w-full h-full">
                                    <div className="bg-gray-700 overflow-hidden">{images.topLeft ? <img src={images.topLeft} className="w-full h-full object-cover" style={{transform: `scale(${imageTransforms.topLeft.scale}) translateX(${imageTransforms.topLeft.x}%) translateY(${imageTransforms.topLeft.y}%)`}}/> : <div className="w-full h-full bg-gray-700"></div>}</div>
                                    <div className="bg-gray-600 overflow-hidden">{images.topRight ? <img src={images.topRight} className="w-full h-full object-cover" style={{transform: `scale(${imageTransforms.topRight.scale}) translateX(${imageTransforms.topRight.x}%) translateY(${imageTransforms.topRight.y}%)`}}/> : <div className="w-full h-full bg-gray-600"></div>}</div>
                                    <div className="bg-gray-600 overflow-hidden">{images.bottomLeft ? <img src={images.bottomLeft} className="w-full h-full object-cover" style={{transform: `scale(${imageTransforms.bottomLeft.scale}) translateX(${imageTransforms.bottomLeft.x}%) translateY(${imageTransforms.bottomLeft.y}%)`}}/> : <div className="w-full h-full bg-gray-600"></div>}</div>
                                    <div className="bg-gray-700 overflow-hidden">{images.bottomRight ? <img src={images.bottomRight} className="w-full h-full object-cover" style={{transform: `scale(${imageTransforms.bottomRight.scale}) translateX(${imageTransforms.bottomRight.x}%) translateY(${imageTransforms.bottomRight.y}%)`}}/> : <div className="w-full h-full bg-gray-700"></div>}</div>
                                </div>
                                <div className="absolute top-10 left-10" style={{backgroundColor: settings.showTextBg ? `rgba(${parseInt(settings.textBgColor.slice(1,3),16)},${parseInt(settings.textBgColor.slice(3,5),16)},${parseInt(settings.textBgColor.slice(5,7),16)},${settings.textBgOpacity})` : 'transparent', padding: `${settings.textBgPadding}px`, border: settings.showTextBg && settings.textBgBorder ? `${settings.textBgBorderWidth}px solid ${settings.textBgBorderColor}` : 'none'}}><p className="text-4xl font-bold" style={{color: settings.nameColor, textShadow: '2px 2px 4px rgba(0,0,0,0.7)'}}>{tritypeToDisplay.id} - {tritypeToDisplay.name}</p></div>
                                <div className="absolute bottom-10 right-10 text-right" style={{backgroundColor: settings.showTextBg ? `rgba(${parseInt(settings.textBgColor.slice(1,3),16)},${parseInt(settings.textBgColor.slice(3,5),16)},${parseInt(settings.textBgColor.slice(5,7),16)},${settings.textBgOpacity})` : 'transparent', padding: `${settings.textBgPadding}px`, border: settings.showTextBg && settings.textBgBorder ? `${settings.textBgBorderWidth}px solid ${settings.textBgBorderColor}` : 'none'}}><p className="text-3xl italic" style={{color: settings.keywordsColor, textShadow: '2px 2px 4px rgba(0,0,0,0.7)'}}>{tritypeToDisplay.keywords}</p></div>
                                <div className="absolute top-1/2 left-1/2 w-[40%] h-[40%] -translate-x-1/2 -translate-y-1/2" style={{opacity: settings.enneagramOpacity}}>
                                    <EnneagramSymbol color={tritypeToDisplay.color} tritypeId={tritypeToDisplay.id} bgImage={settings.enneagramBgImage} fontColor={settings.enneagramNumberColor}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
